import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, Upload, Camera, ShieldCheck, CurrencyEur, IdentificationCard, Spinner, EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API } from '@/App';
import { useLanguage } from '@/i18n/LanguageContext';
import { getT } from '@/i18n/translations';

const VerificationWizard = ({ token, onComplete }) => {
  const { language } = useLanguage();
  const copy = getT('verification', language);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [uploads, setUploads] = useState({
    idFront: false,
    idBack: false,
    selfie: false
  });
  const [complete, setComplete] = useState(false);

  const totalSteps = 2;
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const readPayload = async (res) => {
    const text = await res.text();
    return text ? JSON.parse(text) : {};
  };

  const post = async (url, body = {}) => {
    const res = await fetch(`${API}${url}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
    const payload = await readPayload(res);

    if (!res.ok) {
      throw new Error(payload.detail || 'Verification failed');
    }
    return payload;
  };

  const uploadIdentityPart = async (part, file) => {
    if (!file) return;
    setLoading(true);
    try {
      const documentType = {
        idFront: 'id_front',
        idBack: 'id_back',
        selfie: 'selfie'
      }[part];
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/auth/verify/upload-document/${documentType}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const payload = await readPayload(res);
      if (!res.ok) throw new Error(payload.detail || 'Upload failed');
      setUploads(prev => ({ ...prev, [part]: true }));
      toast.success(`${copy.uploaded}: ${file.name}`);
    } catch (error) {
      toast.error(error.message);
    }
    setLoading(false);
  };

  const continueFromIdentity = () => {
    if (!uploads.idFront || !uploads.idBack || !uploads.selfie) {
      toast.error('Upload the front, back of your ID and a selfie');
      return;
    }
    setStep(2);
  };

  const verifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await post('/auth/verify/email-code', { code: emailCode });
      setComplete(true);
      toast.success('Email confirmed');
    } catch (error) {
      toast.error(error.message);
    }
    setLoading(false);
  };

  const resendEmailCode = async () => {
    setResendLoading(true);
    try {
      const payload = await post('/auth/verify/resend-email-code');
      setResendCooldown(payload.cooldownSeconds || 60);
      toast.success(payload.emailDeliveryConfigured ? 'Code sent' : 'Code regenerated for dev testing');
    } catch (error) {
      toast.error(error.message);
    }
    setResendLoading(false);
  };

  const stepIndicator = (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2].map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300
            ${step > s || complete ? 'bg-[#cba135] text-[#0e0b04]' : step === s ? 'bg-[#cba135]/20 border-2 border-[#cba135] text-[#cba135]' : 'border border-[#cba135]/20 text-[#cba135]/30'}`}>
            {step > s || complete ? <CheckCircle size={16} weight="bold" /> : s}
          </div>
          {i < 1 && <div className={`w-6 h-0.5 ${step > s || complete ? 'bg-[#cba135]' : 'bg-[#cba135]/15'}`} />}
        </div>
      ))}
    </div>
  );

  if (complete) {
    return (
      <div className="space-y-4 text-center" data-testid="verification-wizard">
        {stepIndicator}
        <CheckCircle size={56} weight="duotone" className="text-[#cba135] mx-auto" />
        <h3 className="heading-font text-xl font-medium text-[#cba135]">{copy.doneTitle}</h3>
        <p className="text-sm text-[#cba135]/60">{copy.doneDesc}</p>
        <Button
          data-testid="go-to-dashboard-button"
          className="w-full bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold"
          onClick={onComplete}
        >
          <CurrencyEur size={20} className="mr-2" />
          {copy.goToDashboard}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="verification-wizard">
      {stepIndicator}
      <p className="text-center text-xs text-[#cba135]/50 mb-2">{copy.stepLabel} {step} {copy.of} {totalSteps}</p>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="flex items-center gap-2">
              <IdentificationCard size={22} weight="duotone" className="text-[#cba135]" />
              <h3 className="heading-font text-lg font-medium text-[#cba135]">{copy.step1Title}</h3>
            </div>
            <p className="text-sm text-[#cba135]/60">{copy.step1Desc}</p>
            {[
              ['idFront', copy.idFront, Upload, 'image/jpeg,image/png,application/pdf'],
              ['idBack', copy.idBack, Upload, 'image/jpeg,image/png,application/pdf'],
              ['selfie', copy.selfie, Camera, 'image/jpeg,image/png']
            ].map(([key, label, Icon, accept]) => (
              <label
                key={key}
                data-testid={`upload-${key}`}
                className="w-full border-2 border-dashed border-[#cba135]/20 rounded-lg p-5 text-left hover:border-[#cba135]/40 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {uploads[key] ? <CheckCircle size={28} weight="duotone" className="text-[#cba135]" /> : <Icon size={28} weight="duotone" className="text-[#cba135]/50" />}
                  <div>
                    <p className="text-sm text-[#cba135]">{label}</p>
                    <p className="text-xs text-[#cba135]/50">{uploads[key] ? copy.uploaded : copy.upload}</p>
                  </div>
                </div>
                <input
                  type="file"
                  accept={accept}
                  className="sr-only"
                  disabled={loading}
                  onChange={(event) => uploadIdentityPart(key, event.target.files?.[0])}
                />
              </label>
            ))}
            <Button
              type="button"
              onClick={continueFromIdentity}
              className="w-full bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold"
              disabled={loading}
            >
              {copy.next}
            </Button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.form
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            onSubmit={verifyEmail}
            className="space-y-4"
          >
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={22} weight="duotone" className="text-[#cba135]" />
              <h3 className="heading-font text-lg font-medium text-[#cba135]">{copy.step3Title}</h3>
            </div>
            <p className="text-sm text-[#cba135]/60">{copy.step3Desc}</p>
            <div>
              <Label className="text-[#cba135] text-sm">{copy.emailCode}</Label>
              <Input
                data-testid="verify-email-code"
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value)}
                className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                placeholder="123456"
                required
              />
            </div>
            <div className="flex items-center gap-2 text-xs text-[#cba135]/50">
              <ShieldCheck size={14} weight="fill" />
              <span>Confirming this code verifies your email address and activates your account.</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                type="button"
                variant="outline"
                className="border-[#cba135]/25 bg-transparent text-[#cba135] hover:bg-[#cba135]/10"
                disabled={loading || resendLoading || resendCooldown > 0}
                onClick={resendEmailCode}
              >
                {resendLoading ? <Spinner size={20} className="animate-spin mx-auto" /> : resendCooldown > 0 ? `${copy.resendIn || 'Resend in'} ${resendCooldown}s` : (copy.resendCode || 'Resend code')}
              </Button>
              <Button className="bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold" disabled={loading}>
                {loading ? <Spinner size={20} className="animate-spin mx-auto" /> : 'Complete registration'}
              </Button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VerificationWizard;
